import React, { useEffect, useState, useCallback } from 'react'
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  CircularProgress,
  Switch,
  Snackbar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Link,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'

interface EventPartDto {
  id: string
  name: string
  date: string | null
}

interface ParticipantAttendanceDto {
  participationId: string
  firstName: string
  lastName: string
  companyName: string | null
  registeredEventPartIds: string[]
  attendances: { [key: string]: boolean }
}

interface EventAttendanceDto {
  eventId: string
  eventName: string
  eventParts: EventPartDto[]
  participants: ParticipantAttendanceDto[]
}

const Attendance: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<EventAttendanceDto | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEventPartId, setSelectedEventPartId] = useState<string>('all')
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackMsg, setSnackMsg] = useState('')
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success')
  const [updating, setUpdating] = useState<string | null>(null) // participationId + eventPartId

  const requestHeaders = useCallback(() => {
    const headers: HeadersInit = new Headers()
    headers.set('Accept', 'application/json')
    headers.set('Content-Type', 'application/json')
    addCsrfHeaders(headers)
    return headers
  }, [])

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetch(`${Env.basePath}/api/public/events/current/attendance`, {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders(),
      })
      if (!res.ok) throw new Error(`Fehler beim Laden: ${res.status}`)
      const json: EventAttendanceDto = await res.json()
      setData(json)
    } catch (err: any) {
      console.error(err)
      setSnackMsg(err.message || 'Anwesenheitsliste konnte nicht geladen werden.')
      setSnackSeverity('error')
      setSnackOpen(true)
    } finally {
      setLoading(false)
    }
  }, [requestHeaders])

  useEffect(() => {
    fetchAttendance()
  }, [fetchAttendance])

  const handleToggleAttendance = async (participationId: string, eventPartId: string, currentPresent: boolean) => {
    const updateKey = `${participationId}-${eventPartId}`
    setUpdating(updateKey)
    const newPresent = !currentPresent

    try {
      const url = `${Env.basePath}/api/public/events/attendance?participationId=${participationId}&eventPartId=${eventPartId}&present=${newPresent}`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders(),
      })

      if (!res.ok) throw new Error(`Update fehlgeschlagen: ${res.status}`)

      // Update local state
      setData(prev => {
        if (!prev) return null
        return {
          ...prev,
          participants: prev.participants.map(p => {
            if (p.participationId === participationId) {
              return {
                ...p,
                attendances: {
                  ...p.attendances,
                  [eventPartId]: newPresent,
                },
              }
            }
            return p
          }),
        }
      })

      setSnackMsg('Anwesenheit aktualisiert.')
      setSnackSeverity('success')
      setSnackOpen(true)
    } catch (err: any) {
      console.error(err)
      setSnackMsg(err.message || 'Fehler beim Aktualisieren der Anwesenheit.')
      setSnackSeverity('error')
      setSnackOpen(true)
    } finally {
      setUpdating(null)
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='50vh'>
        <CircularProgress />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box textAlign='center' py={4}>
        <Typography variant='h6'>Keine Event-Daten verfügbar.</Typography>
      </Box>
    )
  }

  const filteredParticipants = data.participants.filter(p => {
    const searchStr = searchTerm.toLowerCase()
    return (
      p.firstName.toLowerCase().includes(searchStr) ||
      p.lastName.toLowerCase().includes(searchStr) ||
      (p.companyName && p.companyName.toLowerCase().includes(searchStr))
    )
  })

  const filteredEventParts = data.eventParts.filter(part =>
    selectedEventPartId === 'all' || part.id === selectedEventPartId,
  )

  return (
    <Grid container sx={{ mt: 3, mb: 4 }}>
      <Grid item xs={12}>
        <h2>{data.eventName}</h2>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label='Teilnehmer suchen (Name oder Firma)'
            variant='outlined'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder='Vorname, Nachname oder Firma eingeben...'
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size='small'>
            <InputLabel id='event-part-filter-label'>Programmpunkt Filter</InputLabel>
            <Select
              labelId='event-part-filter-label'
              value={selectedEventPartId}
              label='Programmpunkt Filter'
              onChange={(e) => setSelectedEventPartId(e.target.value as string)}
            >
              <MenuItem value='all'>Alle Programmpunkte</MenuItem>
              {data.eventParts.map(part => (
                <MenuItem key={part.id} value={part.id}>
                  {part.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {!isMobile ? (
        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
          <Table stickyHeader size={selectedEventPartId !== 'all' ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Teilnehmer</TableCell>
                {selectedEventPartId === 'all' && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Unternehmen</TableCell>
                )}
                {filteredEventParts.map(part => (
                  <TableCell key={part.id} align='center' sx={{ fontWeight: 'bold' }}>
                    {part.name}
                    {part.date && (
                      <Typography variant='caption' display='block' color='text.secondary'>
                        {new Date(part.date).toLocaleDateString()}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParticipants.map(participant => (
                <TableRow key={participant.participationId} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/markencamp-management/Participation/${participant.participationId}`}
                      underline='hover'
                      color='primary'
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {participant.lastName}, {participant.firstName}
                      </Typography>
                    </Link>
                    {selectedEventPartId !== 'all' && (
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {participant.companyName || '–'}
                      </Typography>
                    )}
                  </TableCell>
                  {selectedEventPartId === 'all' && (
                    <TableCell>{participant.companyName || '–'}</TableCell>
                  )}
                  {filteredEventParts.map(part => {
                    const isRegistered = participant.registeredEventPartIds?.includes(part.id)
                    const isPresent = !!participant.attendances[part.id]
                    const isUpdating = updating === `${participant.participationId}-${part.id}`
                    return (
                      <TableCell key={part.id} align='center'>
                        {!isRegistered ? (
                          <Typography variant='caption' color='text.disabled'>–</Typography>
                        ) : isUpdating ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Switch
                            checked={isPresent}
                            onChange={() => handleToggleAttendance(participant.participationId, part.id, isPresent)}
                            color='primary'
                          />
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {filteredParticipants.map(participant => (
            <Paper key={participant.participationId} sx={{ p: selectedEventPartId !== 'all' ? 1.5 : 2, borderRadius: 2 }}>
              {selectedEventPartId === 'all' ? (
                <>
                  <Box mb={1}>
                    <Link
                      component={RouterLink}
                      to={`/markencamp-management/Participation/${participant.participationId}`}
                      underline='hover'
                      color='primary'
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                        {participant.lastName}, {participant.firstName}
                      </Typography>
                    </Link>
                    <Typography variant='body2' color='text.secondary'>
                      {participant.companyName || 'Kein Unternehmen'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    {filteredEventParts.map(part => {
                      const isRegistered = participant.registeredEventPartIds?.includes(part.id)
                      if (!isRegistered) return null
                      const isPresent = !!participant.attendances[part.id]
                      const isUpdating = updating === `${participant.participationId}-${part.id}`
                      return (
                        <Grid item xs={12} key={part.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant='body2'>
                            {part.name}
                          </Typography>
                          <Box display='flex' alignItems='center'>
                            {isUpdating ? (
                              <CircularProgress size={20} sx={{ mr: 2 }} />
                            ) : (
                              <Switch
                                checked={isPresent}
                                onChange={() => handleToggleAttendance(participant.participationId, part.id, isPresent)}
                                color='primary'
                              />
                            )}
                            <Typography variant='caption' sx={{ width: 45, textAlign: 'right' }}>
                              {isPresent ? 'Anwesend' : 'Fehlt'}
                            </Typography>
                          </Box>
                        </Grid>
                      )
                    })}
                  </Grid>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Link
                      component={RouterLink}
                      to={`/markencamp-management/Participation/${participant.participationId}`}
                      underline='hover'
                      color='primary'
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                        {participant.lastName}, {participant.firstName}
                      </Typography>
                    </Link>
                    <Typography variant='caption' color='text.secondary'>
                      {participant.companyName || '–'}
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center'>
                    {filteredEventParts.map(part => {
                      const isRegistered = participant.registeredEventPartIds?.includes(part.id)
                      const isPresent = !!participant.attendances[part.id]
                      const isUpdating = updating === `${participant.participationId}-${part.id}`
                      return (
                        <React.Fragment key={part.id}>
                          {!isRegistered ? (
                            <Typography variant='caption' color='text.disabled' sx={{ minWidth: 45, textAlign: 'right' }}>–</Typography>
                          ) : isUpdating ? (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          ) : (
                            <>
                              <Switch
                                checked={isPresent}
                                onChange={() => handleToggleAttendance(participant.participationId, part.id, isPresent)}
                                color='primary'
                                size='small'
                              />
                              <Typography variant='caption' sx={{ minWidth: 45, textAlign: 'right' }}>
                                {isPresent ? 'Anwesend' : 'Fehlt'}
                              </Typography>
                            </>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default Attendance
